import React, { useState, useRef, useEffect } from 'react';
import { callFunction } from '../services/api.js';
import styles from './ChatBot.module.css';

const ChatBot = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: 'Hi! I\'m your Vates Domus AI FAQ Assistant.\n\nWhat would you like to know?',
      time: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = { sender: 'user', text: input, time: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await callFunction('supportChat', { 
        query: input,
        context: getCurrentPageContext()
      });

      const aiMsg = { 
        sender: 'ai', 
        text: response.answer, 
        time: new Date(),
        sources: response.sources || [],
        confidence: response.confidence || 0
      };
      setMessages(prev => [...prev, aiMsg]);

    } catch (error) {
      console.error('ChatBot error:', error);
      
      const isRateLimit = error.message.includes('rate limiting') || 
                         error.message.includes('429') ||
                         error.message.includes('Too Many Requests');
      
      let errorText;
      if (isRateLimit) {
        errorText = 'I\'m currently experiencing high demand. Please wait a moment and try again, or contact support at info@vatesdomus.com if this continues.';
      } else {
        errorText = 'Sorry, I encountered an error. Please try again or contact support at info@vatesdomus.com.';
      }
      
      const errorMsg = { 
        sender: 'ai', 
        text: errorText, 
        time: new Date() 
      };
      setMessages(prev => [...prev, errorMsg]);
    }

    setLoading(false);
  };

  const getCurrentPageContext = () => {
    const path = window.location.pathname;
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/chat')) return 'MLS Chat';
    if (path.includes('/map-search')) return 'Map Search';
    if (path.includes('/cma')) return 'CMA';
    return 'General';
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const clearChat = () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      setMessages([
        {
          sender: 'ai',
          text: 'Hi! I\'m your Vates Domus AI FAQ Assistant. I can help answer questions about:\n• Account and billing\n• Features and usage\n• Troubleshooting\n• General support\n\nWhat would you like to know?',
          time: new Date()
        }
      ]);
    }
  };

  return (
    <div className={`${styles.chatbotContainer} ${isExpanded ? 'expanded' : 'collapsed'} ${isMinimized ? styles.minimized : ''}`}>
      {/* Chat bubble button */}
      <div 
        className={styles.chatbotBubble}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className={styles.chatbotIcon}>💬</span>
        {!isMinimized && <span className={styles.chatbotText}>FAQ Assistant</span>}
      </div>

      {/* Chat window */}
      {isExpanded && (
        <div className={styles.chatbotWindow}>
          <div className={styles.chatbotHeader}>
            <h3>Vates Domus AI FAQ Assistant</h3>
            <div className={styles.chatbotControls}>
              <button onClick={clearChat} title="Clear Chat">
                🗑️
              </button>
              <button onClick={() => setIsMinimized(!isMinimized)} title={isMinimized ? 'Expand' : 'Minimize'}>
                {isMinimized ? '📈' : '📉'}
              </button>
              <button onClick={() => setIsExpanded(false)} title="Close">✕</button>
            </div>
          </div>
          
          <div className={styles.chatbotContent}>
            <div className={styles.chatbotMessages}>
              {messages.map((msg, i) => (
                <div key={i} className={`${styles.chatbotMessage} ${styles[msg.sender]}`}>
                  <div className={styles.messageContent}>
                    <div className={styles.messageText}>{msg.text}</div>
                    <div className={styles.messageTime}>{formatTime(msg.time)}</div>
                    {msg.sources && msg.sources.length > 0 && (
                      <div className={styles.messageSources}>
                        <small>Sources: {msg.sources.join(', ')}</small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className={`${styles.chatbotMessage} ${styles.ai}`}>
                  <div className={styles.messageContent}>
                    <div className={styles.typingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>
            
            <form onSubmit={handleSend} className={styles.chatbotInputForm}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me about Vates Domus AI features, billing, or support..."
                disabled={loading}
                className={styles.chatbotInput}
              />
              <button 
                type="submit" 
                disabled={loading || !input.trim()}
                className={styles.chatbotSendBtn}
              >
                {loading ? '...' : '➤'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
